// Single source of truth for the SMC ticket-create wizard.
// Each entry maps a requestType to its display metadata + ordered list of
// form fields. The TypeForm component reads this and dispatches to
// per-type input renderers (text, textarea, select, tags, toggle, file,
// datetime-local).
//
// Drift risk: per-type required-field expectations also live in the
// backend create_smc_ticket_api lambda's REQUIRED_FIELDS map
// (workflow-services/lambdas/create_smc_ticket_api.py). If you change
// `required: true` here, mirror it there.

export const SENDING_CLASSES = ["STD_USPS", "FEDEX", "UPS", "USPS_PRIORITY"];
export const ITEM_TYPES = ["PAGE", "DOCUMENT", "ENVELOPE"];
export const DIVISIONS = ["Retail", "Institutional"];

const DIVISION_FIELD = {
  name: "division", label: "Division", type: "select",
  options: DIVISIONS, required: true, default: "Retail",
};

export const SMC_FORM_SCHEMA = {
  RESCAN: {
    label: "Rescan",
    icon: "/smcIcons/rescan.svg",
    accent: "teal",
    description: "Ask imaging to rescan a page, document, or envelope.",
    fields: [
      DIVISION_FIELD,
      { name: "itemType", label: "Item type", type: "select", options: ITEM_TYPES, required: true },
      { name: "itemIds", label: "Item IDs", type: "tags", required: true,
        placeholder: "type ID and press Enter",
        helpText: "One ID per chip (e.g. 100019983536)" },
      { name: "rescanReason", label: "Reason", type: "textarea", required: true,
        placeholder: "e.g. Image unclear, signature missing on check" },
      { name: "rush", label: "Rush?", type: "toggle", default: false },
      { name: "poBoxNumber", label: "PO Box", type: "text" },
      { name: "trayId", label: "Tray ID", type: "text" },
      { name: "attentionCategory", label: "Attention category", type: "text" },
      { name: "pcn", label: "PCN", type: "text" },
    ],
  },
  PULL: {
    label: "Pull Request",
    icon: "/smcIcons/pull.svg",
    accent: "teal",
    description: "Pull a page or document from archive and ship to a destination.",
    fields: [
      DIVISION_FIELD,
      { name: "itemType", label: "Item type", type: "select", options: ITEM_TYPES, required: true },
      { name: "itemIds", label: "Item IDs", type: "tags", required: true,
        placeholder: "type ID and press Enter" },
      { name: "destination", label: "Destination", type: "textarea", required: true,
        placeholder: "Street; City; State ZIP" },
      { name: "sendingClass", label: "Sending class", type: "select",
        options: SENDING_CLASSES, required: true, default: "STD_USPS" },
      { name: "coverLetterUrl", label: "Cover letter (PDF)", type: "file",
        accept: "application/pdf" },
      { name: "pullReason", label: "Pull reason", type: "textarea" },
      { name: "pageArchiveBox", label: "Page archive box", type: "text" },
      { name: "shelfId", label: "Shelf ID", type: "text" },
      { name: "rush", label: "Rush?", type: "toggle", default: false },
      { name: "pcn", label: "PCN", type: "text" },
    ],
  },
  REJECT_WITH_CORRESPONDENCE: {
    label: "Reject with Correspondence",
    icon: "/smcIcons/reject-corr.svg",
    accent: "navy",
    description: "Mail a check back to the client with a cover letter.",
    fields: [
      DIVISION_FIELD,
      { name: "pageId", label: "Page ID", type: "text", required: true },
      { name: "checkId", label: "Check ID", type: "text", required: true },
      { name: "destination", label: "Destination", type: "textarea", required: true,
        placeholder: "Recipient; Street; City; State ZIP" },
      { name: "sendingClass", label: "Sending class", type: "select",
        options: SENDING_CLASSES, required: true, default: "STD_USPS" },
      { name: "coverLetterUrl", label: "Cover letter (PDF)", type: "file",
        accept: "application/pdf", required: true },
      { name: "checkNumber", label: "Check number", type: "text" },
      { name: "checkAccountNumber", label: "Check account #", type: "text" },
      { name: "amount", label: "Amount", type: "text", placeholder: "0.00" },
      { name: "pageArchiveBox", label: "Page archive box", type: "text" },
      { name: "shelfId", label: "Shelf ID", type: "text" },
      { name: "pcn", label: "PCN", type: "text" },
    ],
  },
  REJECT_WITH_SHRED: {
    label: "Reject with Shred",
    icon: "/smcIcons/reject-shred.svg",
    accent: "navy",
    description: "Destroy a check — no return to client.",
    fields: [
      DIVISION_FIELD,
      { name: "pageId", label: "Page ID", type: "text", required: true },
      { name: "checkId", label: "Check ID", type: "text", required: true },
      { name: "checkNumber", label: "Check number", type: "text" },
      { name: "checkAccountNumber", label: "Check account #", type: "text" },
      { name: "amount", label: "Amount", type: "text", placeholder: "0.00" },
      { name: "pageArchiveBox", label: "Page archive box", type: "text" },
      { name: "shelfId", label: "Shelf ID", type: "text" },
      { name: "coverLetterUrl", label: "Cover letter (optional)", type: "file",
        accept: "application/pdf" },
      { name: "pcn", label: "PCN", type: "text" },
    ],
  },
  PHYSICAL_DEPOSIT: {
    label: "Check Physical Deposit",
    icon: "/smcIcons/physical-deposit.svg",
    accent: "teal",
    description: "Send a physical check for deposit at the bank.",
    fields: [
      DIVISION_FIELD,
      { name: "pageId", label: "Page ID", type: "text" },
      { name: "checkId", label: "Check ID", type: "text", required: true },
      { name: "destination", label: "Destination", type: "textarea", required: true },
      { name: "sendingClass", label: "Sending class", type: "select",
        options: SENDING_CLASSES, required: true, default: "FEDEX" },
      { name: "checkNumber", label: "Check number", type: "text" },
      { name: "checkAccountNumber", label: "Check account #", type: "text" },
      { name: "bankAccountNumber", label: "Bank account #", type: "text" },
      { name: "bank", label: "Bank", type: "text" },
      { name: "checkRoutingNumber", label: "Routing #", type: "text" },
      { name: "amount", label: "Amount", type: "text", placeholder: "0.00" },
      { name: "depositNumber", label: "Deposit number", type: "text" },
    ],
  },
  MOBI_PHYSICAL_DEPOSIT: {
    label: "MOBI Check Physical Deposit",
    icon: "/smcIcons/mobi-deposit.svg",
    accent: "teal",
    description: "Print + ship a mobile-uploaded check for physical deposit.",
    fields: [
      DIVISION_FIELD,
      { name: "mobiCheckId", label: "MOBI check ID", type: "text", required: true },
      { name: "checkId", label: "Check ID", type: "text" },
      { name: "checkImageUrl", label: "Check image", type: "file",
        accept: "image/png,image/jpeg,image/jpg,application/pdf" },
      { name: "checkNumber", label: "Check number", type: "text" },
      { name: "checkAccountNumber", label: "Check account #", type: "text" },
      { name: "bankAccountNumber", label: "Bank account #", type: "text" },
      { name: "bank", label: "Bank", type: "text" },
      { name: "mobiCheckAmount", label: "Amount", type: "text", placeholder: "0.00" },
    ],
  },
  ACKNOWLEDGEMENT: {
    label: "Acknowledgment",
    icon: "/smcIcons/ack.svg",
    accent: "teal",
    description: "Acknowledge a scan-batch resend.",
    fields: [
      DIVISION_FIELD,
      { name: "fileId", label: "File ID (zip filename)", type: "text", required: true,
        placeholder: "SPS-SCANNING-INST-...-...zip" },
      { name: "resendReason", label: "Resend reason", type: "textarea" },
      { name: "newBatchId", label: "New batch ID", type: "text" },
      { name: "dateTime", label: "Date/time", type: "datetime-local" },
    ],
  },
};

export const SMC_TYPE_ORDER = [
  "RESCAN", "PULL",
  "REJECT_WITH_CORRESPONDENCE", "REJECT_WITH_SHRED",
  "PHYSICAL_DEPOSIT", "MOBI_PHYSICAL_DEPOSIT",
  "ACKNOWLEDGEMENT",
];
